/**
 * `<xa-stacked-bar-h>` — horizontal stacked bar chart (per-user /
 * per-operation rows). Registered as `xaStackedBarH`. Single-series data
 * renders as a plain horizontal bar chart (no legend).
 *
 * Bindings:
 * | Binding | Type | Default | Notes |
 * |---|---|---|---|
 * | `data` | `XaCategorical` | — | required; one row per category |
 * | `colors` | `XaPalette` | `'tokens'` | global `xaChartsConfig.palette` wins over the default |
 * | `width` | `number` | `520` | SVG px; height derives from row count; ignored when `auto-width` is on |
 * | `auto-width` | `boolean` | `false` | re-flow to the container width (responsive) |
 * | `row-height` | `number` | `30` | px per row |
 * | `legend` | `'top' \| 'none'` | `'top'` | hidden automatically for single-series data |
 * | `on-series-toggle` | `&` callback | — | `$event: XaSeriesToggleEvent` |
 * | `on-category-hover` | `&` callback | — | `$event: XaHoverEvent` |
 */
import {
  categoricalAriaLabel,
  computeStackedBarH,
  ensureChartStyles,
  legendLayoutFor,
  normalizeCategorical,
  stackedBarHTooltip,
  XA_CHROME,
} from '@xait/charts-core';
import type {
  XaBarHRow,
  XaCategorical,
  XaHoverEvent,
  XaLegendLayout,
  XaPalette,
  XaSeriesToggleEvent,
  XaStackedBarHLayout,
} from '@xait/charts-core';
import { XaChartBaseController, XAC_CATEGORICAL_TABLE, XAC_TIP_TEMPLATE } from './chart-base';
import type { XaCallback } from './chart-base';
import type { XaChartsDefaults } from './config';

class XaStackedBarHController extends XaChartBaseController {
  static override $inject = ['$element', '$scope', 'xaChartsConfig'];

  /* bindings */
  data?: XaCategorical;
  colors?: XaPalette;
  width?: number;
  rowHeight?: number;
  legend?: 'top' | 'none';
  onSeriesToggle!: XaCallback<XaSeriesToggleEvent>;
  onCategoryHover!: XaCallback<XaHoverEvent>;

  /* view state */
  layout: XaStackedBarHLayout | null = null;
  legendPos: 'top' | 'none' = 'top';
  legendLayout: XaLegendLayout = 'row';
  showLegend = false;
  ariaLabel = '';

  private readonly hidden = new Set<string>();
  private hoverIndex: number | null = null;

  constructor(
    $element: ng.IAugmentedJQuery,
    private readonly $scope: ng.IScope,
    private readonly xaChartsConfig: XaChartsDefaults,
  ) {
    super($element);
  }

  $onInit(): void {
    ensureChartStyles();
    this.recompute();
  }

  $onChanges(): void {
    this.recompute();
  }

  override $postLink(): void {
    super.$postLink();
    this.observeAutoWidth(() => this.$scope.$applyAsync(() => this.recompute()));
  }

  $onDestroy(): void {
    this.disconnectAutoWidth();
  }

  toggleSeries(name: string): void {
    const nowHidden = !this.hidden.delete(name);
    if (nowHidden) this.hidden.add(name);
    this.recompute();
    this.onSeriesToggle({ $event: { name, hidden: nowHidden, hiddenAll: Array.from(this.hidden) } });
  }

  isolateSeries(name: string): void {
    const all = (this.layout?.legend ?? []).map((it) => it.label);
    const isolated =
      all.length > 1 && !this.hidden.has(name) && all.every((n) => n === name || this.hidden.has(n));
    this.hidden.clear();
    if (!isolated) {
      for (const n of all) if (n !== name) this.hidden.add(n);
    }
    this.recompute();
    this.onSeriesToggle({
      $event: { name, hidden: this.hidden.has(name), hiddenAll: Array.from(this.hidden) },
    });
  }

  hoverRow(ev: MouseEvent, row: XaBarHRow): void {
    if (!this.layout) return;
    this.showTip(ev, stackedBarHTooltip(this.layout, row.index));
    if (this.hoverIndex !== row.index) {
      this.hoverIndex = row.index;
      this.onCategoryHover({ $event: { categoryIndex: row.index, category: row.category } });
    }
  }

  leaveRows(): void {
    this.hideTip();
    if (this.hoverIndex !== null) {
      this.hoverIndex = null;
      this.onCategoryHover({ $event: { categoryIndex: null } });
    }
  }

  private recompute(): void {
    if (this.data) {
      this.data = normalizeCategorical(this.data);
    }
    this.legendPos = this.legend ?? 'top';
    this.legendLayout = legendLayoutFor('stacked-bar-h', this.legendPos, false);
    // Single-series data renders as a plain horizontal bar chart: legend 'none'.
    this.showLegend = this.legendPos === 'top' && (this.data?.series.length ?? 0) > 1;
    if (!this.data) {
      this.layout = null;
      return;
    }
    this.layout = computeStackedBarH(this.data, {
      width: this.effectiveWidth(this.width ?? 520),
      rowHeight: this.rowHeight ?? 30,
      colors: this.colors ?? this.xaChartsConfig.palette ?? 'tokens',
      hidden: this.hidden,
    });
    this.ariaLabel = categoricalAriaLabel(
      this.data.series.length > 1 ? 'Horizontal stacked bar' : 'Horizontal bar',
      this.data,
    );
  }
}

export const xaStackedBarHComponent: ng.IComponentOptions = {
  controller: XaStackedBarHController,
  bindings: {
    data: '<',
    colors: '<',
    width: '<',
    autoWidth: '<',
    rowHeight: '<',
    legend: '<',
    onSeriesToggle: '&',
    onCategoryHover: '&',
  },
  template: `<div class="xac-chart">
  <xac-legend ng-if="$ctrl.layout && $ctrl.showLegend" items="$ctrl.layout.legend" layout="$ctrl.legendLayout" show-values="false" on-toggle="$ctrl.toggleSeries(label)" on-isolate="$ctrl.isolateSeries(label)"></xac-legend>
  <svg ng-if="$ctrl.layout" class="xac-svg" role="img" aria-label="{{$ctrl.ariaLabel}}" ng-attr-width="{{$ctrl.layout.width}}" ng-attr-height="{{$ctrl.layout.height}}">
    <g class="xac-bargroup" ng-repeat="row in $ctrl.layout.rows track by $index" ng-mousemove="$ctrl.hoverRow($event, row)" ng-mouseleave="$ctrl.leaveRows()">
      <rect fill="transparent" ng-attr-x="{{row.hitX}}" ng-attr-y="{{row.hitY}}" ng-attr-width="{{row.hitWidth}}" ng-attr-height="{{row.hitHeight}}"></rect>
      <text class="xac-rowlabel" text-anchor="end" ng-attr-x="{{row.labelX}}" ng-attr-y="{{row.labelY}}">{{row.category}}</text>
      <line stroke="${XA_CHROME.grid}" ng-attr-x1="{{row.trackX0}}" ng-attr-x2="{{row.trackX1}}" ng-attr-y1="{{row.yMid}}" ng-attr-y2="{{row.yMid}}"></line>
      <rect ng-repeat="seg in row.segments track by seg.seriesName" ng-attr-x="{{seg.x}}" ng-attr-y="{{seg.y}}" ng-attr-width="{{seg.width}}" ng-attr-height="{{seg.height}}" ng-attr-rx="{{seg.rx}}" ng-attr-fill="{{seg.color}}"></rect>
      <text class="xac-valuelabel" ng-attr-x="{{row.totalX}}" ng-attr-y="{{row.totalY}}">{{row.totalLabel}}</text>
    </g>
  </svg>
  ${XAC_CATEGORICAL_TABLE}
  ${XAC_TIP_TEMPLATE}
</div>`,
};
